import { tryDeleteEmptyInlineCode } from './inlineCode';

/**
 * Caret helpers around atomic inline nodes (variable pills / mentions /
 * nested-var chips in the variables table).
 *
 * contenteditable=false atoms leave the caret as a parent-element offset
 * with no adjacent text node. Browsers then often select or unwrap the atom
 * on insertHTML (paste), which looks like the pill "whitening" / breaking.
 *
 * A trailing ZWSP caret buffer keeps paste safe, but native Backspace then
 * "sticks" (first keystroke eats the invisible ZWSP; further ones do nothing
 * at the CE=false boundary). tryDeleteAdjacentAtomic fixes that.
 */

export const ATOMIC_PILL_SELECTOR = '[data-doc-variable], [data-doc-math]';

const isAtomicInline = (node) =>
  node?.nodeType === Node.ELEMENT_NODE &&
  (node.hasAttribute?.('data-doc-variable') ||
    node.hasAttribute?.('data-doc-math') ||
    node.hasAttribute?.('data-doc-mention') ||
    node.hasAttribute?.('data-var-ref'));

/** True when a text node is only zero-width caret buffer(s). */
const isZwspOnly = (node) =>
  node?.nodeType === Node.TEXT_NODE &&
  !!node.textContent &&
  /^[\u200B]+$/.test(node.textContent);

/** Ensure a text node sits after `atomic` and put the caret inside it. */
export const placeCaretAfterAtomic = (atomic) => {
  if (!atomic?.parentNode) return;
  let text = atomic.nextSibling;
  if (!text || text.nodeType !== Node.TEXT_NODE) {
    text = document.createTextNode('\u200B');
    atomic.parentNode.insertBefore(text, atomic.nextSibling);
  } else if (!text.textContent) {
    // Empty text nodes get dropped on innerHTML round-trips; keep a ZWSP.
    text.textContent = '\u200B';
  }
  const after = document.createRange();
  after.setStart(text, text.textContent.length);
  after.collapse(true);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(after);
};

/** Brief pin so a follow-up variable refresh can re-park the caret after insert. */
const pendingCaretByEditable = new WeakMap();

/** Remember which pill the caret should follow for the next ~300ms. */
export const pinCaretAfterPillIndex = (el, index, ms = 300) => {
  if (!el || index < 0) return;
  pendingCaretByEditable.set(el, { index, until: Date.now() + ms });
};

/** Pinned pill index for `el`, or -1 if none / expired. */
export const pinnedCaretPillIndex = (el) => {
  const hit = pendingCaretByEditable.get(el);
  if (!hit) return -1;
  if (Date.now() > hit.until) {
    pendingCaretByEditable.delete(el);
    return -1;
  }
  return hit.index;
};

/**
 * Re-place the caret after an atomic pill once react-contenteditable finishes
 * rewriting innerHTML. That library's componentDidUpdate calls replaceCaret(),
 * which parks the caret at the END of the editable — fine for short paragraphs
 * (end ≈ after the new pill) but wrong in code blocks where the pill sits mid-body.
 *
 * `getEditable` → the contenteditable host; `getPill(el)` → the pill to follow
 * (re-queried each attempt so it survives DOM swaps).
 */
export const schedulePlaceCaretAfterAtomic = (getEditable, getPill) => {
  const restore = () => {
    const el = typeof getEditable === 'function' ? getEditable() : getEditable;
    if (!el?.isConnected) return;
    const pill = typeof getPill === 'function' ? getPill(el) : getPill;
    if (!pill?.isConnected || !el.contains(pill)) return;
    el.focus({ preventScroll: true });
    placeCaretAfterAtomic(pill);
  };
  restore();
  requestAnimationFrame(restore);
  setTimeout(restore, 0);
  setTimeout(restore, 45);
};

/**
 * If the collapsed caret sits in/after a variable pill (or its ZWSP buffer),
 * return that pill's index among `[data-doc-variable], [data-doc-math]` in `el`. Else -1.
 */
export const caretAtomicPillIndex = (el) => {
  if (!el) return -1;
  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed || !el.contains(sel.anchorNode)) {
    return -1;
  }
  const { startContainer, startOffset } = sel.getRangeAt(0);
  let atomic = null;

  if (startContainer.nodeType === Node.TEXT_NODE) {
    if (
      isAtomicInline(startContainer.previousSibling) &&
      (startOffset === 0 || isZwspOnly(startContainer))
    ) {
      atomic = startContainer.previousSibling;
    } else if (isZwspOnly(startContainer) && isAtomicInline(startContainer.previousSibling)) {
      atomic = startContainer.previousSibling;
    }
  } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
    const prev = startContainer.childNodes[startOffset - 1];
    if (isAtomicInline(prev)) atomic = prev;
    else if (isZwspOnly(prev) && isAtomicInline(prev.previousSibling)) {
      atomic = prev.previousSibling;
    }
  }

  if (
    !atomic?.hasAttribute?.('data-doc-variable') &&
    !atomic?.hasAttribute?.('data-doc-math')
  ) {
    return -1;
  }
  const pills = el.querySelectorAll(ATOMIC_PILL_SELECTOR);
  return Array.prototype.indexOf.call(pills, atomic);
};

/**
 * Before insertHTML / typing at a collapsed caret, move into a real text
 * node when the caret is flush against an atomic inline.
 */
export const ensureEditableCaretForInsert = () => {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return;

  const range = sel.getRangeAt(0);
  const { startContainer, startOffset } = range;

  if (startContainer.nodeType === Node.TEXT_NODE) {
    if (startOffset === 0 && isAtomicInline(startContainer.previousSibling)) {
      // Already in a text node after an atom — safe.
      return;
    }
    return;
  }

  if (startContainer.nodeType !== Node.ELEMENT_NODE) return;

  const prev = startContainer.childNodes[startOffset - 1];
  if (!isAtomicInline(prev)) return;

  placeCaretAfterAtomic(prev);
};

/** Remove an atomic pill (+ trailing ZWSP buffer) and park the caret at the join. */
const removeAtomicAndPlaceCaret = (atomic) => {
  const parent = atomic.parentNode;
  if (!parent) return;

  const prev = atomic.previousSibling;
  let next = atomic.nextSibling;
  if (isZwspOnly(next)) {
    const drop = next;
    next = next.nextSibling;
    drop.remove();
  }
  atomic.remove();

  const sel = window.getSelection();
  const range = document.createRange();

  if (prev?.parentNode) {
    if (isAtomicInline(prev)) {
      placeCaretAfterAtomic(prev);
      return;
    }
    if (prev.nodeType === Node.TEXT_NODE) {
      range.setStart(prev, prev.textContent.length);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    range.setStartAfter(prev);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    return;
  }

  if (next?.parentNode && next.nodeType === Node.TEXT_NODE) {
    range.setStart(next, 0);
    range.collapse(true);
  } else {
    const text = document.createTextNode('\u200B');
    parent.insertBefore(text, next || null);
    range.setStart(text, 0);
    range.collapse(true);
  }
  sel.removeAllRanges();
  sel.addRange(range);
};

/**
 * Backspace after (or Delete before) a variable/mention pill: remove the whole
 * atom in one keystroke. Treats a ZWSP-only caret buffer as transparent so the
 * first Backspace doesn't look like a no-op.
 *
 * Returns true when the event was handled (caller should commit innerHTML).
 */
export const tryDeleteAdjacentAtomic = (e) => {
  if (e.key !== 'Backspace' && e.key !== 'Delete') return false;
  if (e.ctrlKey || e.metaKey || e.altKey) return false;

  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return false;

  const { startContainer, startOffset } = sel.getRangeAt(0);
  let atomic = null;

  if (e.key === 'Backspace') {
    if (startContainer.nodeType === Node.TEXT_NODE) {
      // Caret at start of following text, or anywhere inside a ZWSP-only buffer
      if (
        isAtomicInline(startContainer.previousSibling) &&
        (startOffset === 0 || isZwspOnly(startContainer))
      ) {
        atomic = startContainer.previousSibling;
      }
    } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
      const prev = startContainer.childNodes[startOffset - 1];
      if (isAtomicInline(prev)) {
        atomic = prev;
      } else if (isZwspOnly(prev) && isAtomicInline(prev.previousSibling)) {
        atomic = prev.previousSibling;
      }
    }
  } else if (startContainer.nodeType === Node.TEXT_NODE) {
    const text = startContainer.textContent || '';
    if (
      isAtomicInline(startContainer.nextSibling) &&
      (startOffset === text.length || isZwspOnly(startContainer))
    ) {
      atomic = startContainer.nextSibling;
    }
  } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
    const next = startContainer.childNodes[startOffset];
    if (isAtomicInline(next)) {
      atomic = next;
    } else if (isZwspOnly(next) && isAtomicInline(next.nextSibling)) {
      atomic = next.nextSibling;
    }
  }

  if (!atomic) return tryDeleteEmptyInlineCode(e);

  e.preventDefault();
  removeAtomicAndPlaceCaret(atomic);
  return true;
};
